import React, { useEffect, useState, useMemo } from "react";
import styles from "./MultiAddTransactionsModal.module.css";
import { TagsAPI, TransactionsAPI, SystemAPI } from "../../../../../utils/firebase";
import { normalizeFullPath } from "../../UploadDataComponent/utils/tagUtils";
import { parseAmountValue } from "../TransactionsTableComponent/TransactionsTableComponent";
import { DescriptionTypeaheadInput, TagsMultiTypeaheadInput } from "../utils/TypeaheadInputs";

/** "yyyy-MM-dd" for <input type="date" /> */
function todayISO() { return new Date().toISOString().split("T")[0]; }

function normalizeTxnTypes(systemTypes = []) {
  const out = [];
  for (const t of systemTypes) {
    const key = String(t?.key || t?.code || t?.id || t?.name || t?.label || "").trim();
    if (!key) continue;
    const label = String(t?.label || t?.name || key).trim();
    const lc = label.toLowerCase();
    const isTransfer = Boolean(t?.isTransfer ?? /transfer/.test(lc));
    let sign = (typeof t?.sign === "number" && (t.sign === 1 || t.sign === -1 || t.sign === 0)) ? t.sign : null;
    if (sign == null) sign = /expense|debit/.test(lc) ? -1 : /income|credit|refund/.test(lc) ? +1 : isTransfer ? 0 : 0;
    out.push({ key, label, isTransfer, sign });
  }
  const byKey = new Map(); for (const t of out) byKey.set(t.key, t);
  return Array.from(byKey.values());
}

function TypeButton({ label, signHint, onPick, menu }) {
  const [open, setOpen] = useState(false);
  const sign = signHint < 0 ? "−" : signHint > 0 ? "+" : "±";
  return (
    <div className="dropdown">
      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setOpen(!open)}>
        {sign}
      </button>
      {open && (
        <ul className="dropdown-menu show" style={{ position: "absolute" }}>
          {menu.map((m) => (
            <li key={m.key}>
              <button type="button" className="dropdown-item" onClick={()=>{onPick(m);setOpen(false);}}>
                {m.label.toUpperCase()}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// small helper
function coerceTagArrayFromComma(val) {
  return String(val || "")
    .split(",")
    .map((s) => normalizeFullPath(s.trim()))
    .filter(Boolean);
}

export default function MultiAddTransactionsModal({ user, open, onClose, onSaved, accounts: accountsProp = [] }) {
  // accounts from parent
  const [accounts, setAccounts] = useState([]);
  useEffect(() => { setAccounts(Array.isArray(accountsProp) ? accountsProp : []); }, [accountsProp]);

  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [txnTypes, setTxnTypes] = useState([]);
  const [descSuggestions, setDescSuggestions] = useState([]);

  // system txn types
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const typesRaw = await SystemAPI.fetchTransactionTypes();
        if (alive) setTxnTypes(normalizeTxnTypes(typesRaw));
      } catch { if (alive) setTxnTypes([]); }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!open || !user?.email) return;
    const unsub = TagsAPI.subscribeUserTags({
      userEmail: user.email,
      cap: 1000,
      onData: (docs) => {
        const fulls = [];
        for (const t of docs || []) {
          const p = normalizeFullPath(t.fullPath || t.name || "");
          if (p) fulls.push(p);
        }
        setTagSuggestions(fulls.sort((a,b)=>a.localeCompare(b)));
      },
      onError: () => setTagSuggestions([]),
    });
    return () => { try { unsub && unsub(); } catch {} };
  }, [open, user]);

  useEffect(() => {
    let alive = true;
    async function loadDescs() {
      if (!open || !user?.email) return;
      try {
        const rows = await TransactionsAPI.fetchUserTransactionsRange({
          userEmail: user.email, cap: 500, latestFirst: true,
        });
        if (!alive) return;
        const seen = new Set(); const uniq = [];
        for (const r of Array.isArray(rows) ? rows : []) {
          const d = (r.description || "").trim(); if (!d) continue;
          const dl = d.toLowerCase(); if (!seen.has(dl)) { seen.add(dl); uniq.push(d); }
          if (uniq.length >= 100) break;
        }
        setDescSuggestions(uniq);
      } catch { if (alive) setDescSuggestions([]); }
    }
    loadDescs();
    return () => { alive = false; };
  }, [open, user]);

  const defaultDate = todayISO();
  const defaultAccountId = accounts?.[0]?.id || "";

  const typeMenu = useMemo(() => {
    const list = [...txnTypes];
    const orderScore = (t) => {
      if (t.isTransfer) return 1;
      if (!t.isTransfer && t.sign === -1) return 2;
      if (!t.isTransfer && t.sign === +1) return 3;
      return 9;
    };
    return list.sort((a,b)=>orderScore(a)-orderScore(b));
  }, [txnTypes]);

  const defaultKind = typeMenu.find(t => !t.isTransfer && t.sign === -1) || typeMenu[0];

  const EMPTY = () => ({
    kindKey: defaultKind?.key || "EXPENSE",
    description: "",
    tags: "",
    amount: "",
    date: defaultDate,
    accountId: defaultAccountId,   // non-transfer
    accountIdSource: "",           // transfer
    accountIdDestination: "",      // transfer
  });

  const [rows, setRows] = useState([EMPTY(), EMPTY(), EMPTY(), EMPTY(), EMPTY()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setRows([EMPTY(), EMPTY(), EMPTY(), EMPTY(), EMPTY()]);
      setErr(null); setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accounts?.length, txnTypes.length]);

  const onField = (idx, key) => (eOrValue) => {
    const v = (eOrValue && typeof eOrValue === "object" && "target" in eOrValue)
      ? eOrValue.target.value
      : eOrValue;
    setRows((r) => {
      const next = r.slice();
      next[idx] = { ...next[idx], [key]: v };
      return next;
    });
  };

  const onKindPick = (idx, pick) => {
    setRows((r) => {
      const next = r.slice();
      next[idx] = { ...next[idx], kindKey: pick.key };
      return next;
    });
  };

  const addRow = () => setRows((r) => [...r, EMPTY()]);
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

  const canSubmit = rows.some((r) => r.description || r.amount);

  const onSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;

    setBusy(true); setErr(null);
    try {
      const created = [];

      for (const r of rows) {
        const amtParsed = parseAmountValue(r.amount);
        const hasAmt = Number.isFinite(amtParsed);
        if (!r.description && !hasAmt) continue;

        const type = txnTypes.find((t) => t.key === r.kindKey) || {};
        const tagPath = coerceTagArrayFromComma(r.tags);

        if (type.isTransfer) {
          const source = accounts.find((a) => a.id === r.accountIdSource);
          const dest   = accounts.find((a) => a.id === r.accountIdDestination);
          if (!source || !dest || source.id === dest.id || !hasAmt) continue;

          // Use single-unit API for grouping
          const res = await TransactionsAPI.createTransfer({
            user,
            fromAccountId: source.id,
            toAccountId: dest.id,
            amountAbs: Math.abs(amtParsed),
            currencyCode: source.currencyCode || dest.currencyCode,
            date: r.date || defaultDate,
            description: r.description || "(untitled)",
            tagPath,
          });
          created.push(res || { typeName: "Transfer", fromAccountId: source.id, toAccountId: dest.id, amountAbs: Math.abs(amtParsed), date: r.date || defaultDate, description: r.description || "(untitled)", tagPath });
        } else {
          const acct = accounts.find((a) => a.id === r.accountId);
          if (!acct || !hasAmt) continue;
          const signedAmount = type.sign < 0 ? -Math.abs(amtParsed) : Math.abs(amtParsed);
          const tx = {
            typeName: type.sign < 0 ? "Expense" : "Income",
            description: r.description || "(untitled)",
            tagPath,
            date: r.date || defaultDate,
            source: "manual",
            amount: signedAmount,
            accountId: acct.id,
            accountName: acct.name || "",
            currencyCode: acct.currencyCode,
          };
          const res = await TransactionsAPI.createManualTransaction({ user, tx });
          created.push(res || tx);
        }
      }

      if (!created.length) { setBusy(false); return; }
      onSaved?.(created);
      onClose?.();
    } catch (e2) {
      setErr(e2);
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`modal d-block ${styles.modalBackdrop}`} role="dialog" aria-modal="true">
      <div className={`modal-dialog modal-xl modal-dialog-centered ${styles.dialog}`}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Add Transactions</h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose}/>
          </div>

          <div className="modal-body">
            {err && <div className="alert alert-danger py-2">{String(err?.message || err)}</div>}

            <div className={`table-responsive ${styles.lineTableWrap}`}>
              <table className={`table align-middle mb-0 ${styles.lineTable}`}>
                <thead className="table-light">
                  <tr>
                    <th style={{width:"26%"}}>Description</th>
                    <th style={{width:"20%"}}>Tags</th>
                    <th style={{width:"16%"}}>Amount / Type</th>
                    <th style={{width:"14%"}}>Date</th>
                    <th style={{width:"20%"}}>Account / Transfer</th>
                    <th style={{width:"4%"}} aria-label="remove row"></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => {
                    const type = txnTypes.find((t) => t.key === r.kindKey) || {};
                    const isTransfer = !!type.isTransfer;
                    return (
                      <tr key={idx}>
                        <td>
                          <DescriptionTypeaheadInput
                            value={r.description}
                            onChange={onField(idx,"description")}
                            suggestions={descSuggestions}
                            placeholder=""
                          />
                        </td>
                        <td>
                          <TagsMultiTypeaheadInput
                            value={r.tags}
                            onChange={onField(idx,"tags")}
                            suggestions={tagSuggestions}
                            placeholder="comma-separated"
                            maxMatches={5}
                          />
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <TypeButton
                              label={type.label || "Type"}
                              signHint={type.sign ?? 0}
                              menu={typeMenu}
                              onPick={(pick)=>onKindPick(idx, pick)}
                            />
                            <input className="form-control" style={{minWidth:120}} inputMode="decimal" placeholder={isTransfer ? "positive" : "-exp/+inc"} value={r.amount} onChange={onField(idx,"amount")} />
                          </div>
                        </td>
                        <td>
                          <input type="date" className="form-control" value={r.date} onChange={onField(idx,"date")} />
                        </td>
                        <td>
                          {!isTransfer ? (
                            <select className="form-select" value={r.accountId} onChange={onField(idx,"accountId")}>
                              <option value="">Select…</option>
                              {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                            </select>
                          ) : (
                            <div className="d-flex gap-2">
                              <select className="form-select" value={r.accountIdSource} onChange={onField(idx,"accountIdSource")}>
                                <option value="">Source…</option>
                                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                              </select>
                              <select className="form-select" value={r.accountIdDestination} onChange={onField(idx,"accountIdDestination")}>
                                <option value="">Dest…</option>
                                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                              </select>
                            </div>
                          )}
                        </td>
                        <td className="text-end">
                          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={()=>removeRow(idx)}>×</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-2 d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={addRow}>Add Row</button>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>Cancel</button>
            <button type="button" className="btn btn-primary" onClick={onSubmit} disabled={!canSubmit || busy}>
              Add Transactions
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
